import React from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Label, Input, FormFeedback } from 'reactstrap'
import CepField from '../CepField'
import PhoneField from '../PhoneField'
import CitySelect from '../CitySelect'
import SearchableSelect from '../SearchableSelect'
import { UFS } from '../../services/ibgeService'
import { applyDateMask } from '../../../pages/Profile/utils/dateUtils'

const FormField = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  error,
  placeholder,
  required = false,
  disabled = false,
  options = [],
  labelCol = 2,
  inputCol = 10,
  children,
  onAddressChange,
  ...props
}) => {
  // Real HTML semantics for required fields (screen readers, browser hints),
  // not just the red asterisk in the label.
  const requiredAttrs = required ? { required: true, 'aria-required': true } : {}

  const renderInput = () => {
    if (type === 'cep') {
      return (
        <CepField
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          onAddressChange={onAddressChange}
          placeholder={placeholder}
          invalid={!!error}
          disabled={disabled}
          {...props}
        />
      )
    }

    if (type === 'phone') {
      return (
        <PhoneField
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          invalid={!!error}
          disabled={disabled}
          {...props}
        />
      )
    }

    if (type === 'uf-select') {
      return (
        <Input
          type="select"
          id={name}
          name={name}
          value={value || ''}
          onChange={onChange}
          invalid={!!error}
          disabled={disabled}
          {...props}
        >
          <option value="">{placeholder || 'Selecione o estado'}</option>
          {UFS.map(uf => (
            <option key={uf.value} value={uf.value}>{uf.value} — {uf.label}</option>
          ))}
        </Input>
      )
    }

    if (type === 'searchable-select') {
      return (
        <SearchableSelect
          id={name}
          name={name}
          value={value}
          options={options}
          onChange={onChange}
          placeholder={placeholder}
          invalid={!!error}
          disabled={disabled}
          required={required}
          {...props}
        />
      )
    }

    if (type === 'city-select') {
      const { uf, ...rest } = props
      return (
        <CitySelect
          id={name}
          name={name}
          value={value}
          uf={uf}
          onChange={onChange}
          placeholder={placeholder}
          invalid={!!error}
          disabled={disabled}
          {...rest}
        />
      )
    }

    if (type === 'select') {
      return (
        <Input
          type="select"
          id={name}
          name={name}
          value={value || ''}
          onChange={onChange}
          invalid={!!error}
          disabled={disabled}
          {...requiredAttrs}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      )
    }

    if (type === 'textarea') {
      return (
        <Input
          type="textarea"
          id={name}
          name={name}
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          invalid={!!error}
          disabled={disabled}
          {...props}
        />
      )
    }

    if (type === 'checkbox') {
      return (
        <div className="form-check">
          <Input
            type="checkbox"
            id={name}
            name={name}
            checked={!!value}
            onChange={onChange}
            invalid={!!error}
            disabled={disabled}
            className="form-check-input"
            {...props}
          />
          <Label htmlFor={name} className="form-check-label">
            {label}
          </Label>
        </div>
      )
    }

    if (type === 'date-mask') {
      const handleDateChange = (e) => {
        const masked = applyDateMask(e.target.value);
        onChange({ target: { name, value: masked } });
      };
      return (
        <Input
          type="text"
          id={name}
          name={name}
          value={value || ''}
          onChange={handleDateChange}
          placeholder={placeholder}
          invalid={!!error}
          disabled={disabled}
          maxLength={10}
          inputMode="numeric"
          autoComplete="off"
          {...requiredAttrs}
          {...props}
        />
      )
    }

    return (
      <Input
        type={type}
        id={name}
        name={name}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        invalid={!!error}
        disabled={disabled}
        {...requiredAttrs}
        {...props}
      />
    )
  }

  if (type === 'checkbox') {
    return (
      <Row className="mb-4">
        <Col md={{ size: inputCol, offset: labelCol }}>
          {children || renderInput()}
          {error && <FormFeedback>{error}</FormFeedback>}
        </Col>
      </Row>
    )
  }

  return (
    <Row className="mb-4">
      <Label htmlFor={name} className={`col-md-${labelCol} col-form-label`}>
        {label} {required && <span className="text-danger">*</span>}
      </Label>
      <Col md={inputCol}>
        {children || renderInput()}
        {error && <FormFeedback>{error}</FormFeedback>}
      </Col>
    </Row>
  )
}

FormField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  options: PropTypes.array,
  labelCol: PropTypes.number,
  inputCol: PropTypes.number,
  children: PropTypes.node,
  onAddressChange: PropTypes.func
}

export default FormField